import React, { useCallback, useContext, useEffect, useState } from 'react';

import { reloadRoadmapPosts } from 'common/actions/roadmapPosts';
import Error from 'common/common/Error';
import { CompanyContext } from 'common/containers/CompanyContainer';
import ContentContainer from 'common/containers/ContentContainer';
import { LocationContext } from 'common/containers/RouterContainer';
import connect from 'common/core/connect';
import PublicPrioritizationRoadmapHelmet from 'common/helmets/PublicPrioritizationRoadmapHelmet';
import AdminRoadmapTable from 'common/subdomain/admin/AdminRoadmap/AdminRoadmapTable';
import { getQueryFilterParams } from 'common/util/filterPosts';
import findStringMatches from 'common/util/findStringMatches';

import PublicPrioritizationHeader from './PrioritizationRoadmap/PublicPrioritizationHeader';

import type { Board } from 'common/api/endpoints/boards';
import type { RoadmapPost } from 'common/api/endpoints/roadmapPosts';
import type { Roadmap } from 'common/api/endpoints/roadmaps';
import type { StrippedUser } from 'common/api/endpoints/users';
import type { CustomPostField } from 'common/api/resources/postFields';
import type { RoadmapPostState } from 'common/reducers/roadmapPosts';
import type { Option } from 'common/ui/common/select/SelectCommon';

import 'css/components/subdomain/public/_PublicPrioritizationRoadmap.scss';

export type ViewableRoadmapPostsState = Omit<
  RoadmapPostState,
  'loading' | 'error' | 'lastUpdated' | 'queryParams'
>;

type Props = {
  boards: Board[];
  customPostFields: CustomPostField[];
  roadmap: Roadmap;
  reloadRoadmapPosts: (
    queryParams: { [key: string]: unknown },
    supplementalOwners: StrippedUser[]
  ) => void;
  roadmapPosts: RoadmapPostState;
  roadmapPostsLoading: boolean;
};

const PublicPrioritizationRoadmap = ({
  boards,
  roadmapPosts,
  roadmap,
  customPostFields,
  reloadRoadmapPosts,
  roadmapPostsLoading,
}: Props) => {
  const [viewableRoadmapPostsState, setViewableRoadmapPostsState] =
    useState<ViewableRoadmapPostsState>({
      distinctBoards: [],
      distinctCategories: [],
      distinctOwners: [],
      posts: [],
      statuses: [],
    });

  const company = useContext(CompanyContext);
  const location = useContext(LocationContext);

  const [groupBy, setGroupBy] = useState<Option | undefined>(undefined);

  // If the list of roadmap posts changes, show everything
  useEffect(() => {
    setViewableRoadmapPostsState(roadmapPosts);
    setGroupBy(undefined);
  }, [roadmapPosts]);

  const reloadWithFilterQuery = useCallback(() => {
    if (!roadmap?.filterQuery) {
      return;
    }

    const supplementalOwners: StrippedUser[] = [];
    for (const roadmapPost of roadmapPosts.posts) {
      const { owner, ownerID } = roadmapPost.post;
      if (ownerID && owner) {
        supplementalOwners.push(owner);
      }
    }

    const searchParams = new URLSearchParams(roadmap.filterQuery);
    const filterMap: Record<string, any> = {};
    for (const [key, value] of searchParams) {
      filterMap[key] = value;
    }
    location.query = filterMap;
    const queryParams = getQueryFilterParams(null, company, location, {}, roadmap);
    reloadRoadmapPosts(queryParams, supplementalOwners);
  }, [company, location, reloadRoadmapPosts, roadmap, roadmapPosts]);

  useEffect(() => {
    reloadWithFilterQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterRoadmapPosts = (searchValue: string, roadmapPosts: RoadmapPost[]) => {
    if (!searchValue) {
      return roadmapPosts;
    }

    const getTitle = (roadmapPost: RoadmapPost) => roadmapPost.post.title;
    return findStringMatches(roadmapPosts, getTitle, searchValue);
  };

  const searchRoadmapPosts = (searchValue: string) => {
    const filteredPosts = filterRoadmapPosts(searchValue, roadmapPosts.posts);
    setViewableRoadmapPostsState({ ...viewableRoadmapPostsState, posts: filteredPosts });
  };

  if (!roadmap) {
    return (
      <div className="subdomainPrioritizationRoadmapError">
        <Error
          heading="You do not have access to this roadmap"
          subHeading={`Contact ${company.name} to get an invite`}
        />
      </div>
    );
  }

  const noFunc = () => {};

  const posts = roadmapPostsLoading ? [] : viewableRoadmapPostsState?.posts ?? [];

  return (
    <>
      <PublicPrioritizationRoadmapHelmet roadmap={roadmap} />
      <ContentContainer outerClassName="subdomainPrioritizationRoadmap">
        <PublicPrioritizationHeader
          roadmap={roadmap}
          setGroupBy={setGroupBy}
          groupBy={groupBy}
          customPostFields={customPostFields}
          onSearch={searchRoadmapPosts}
        />
        <div className="subdomainPrioritizationRoadmap__table-container">
          <AdminRoadmapTable
            {...viewableRoadmapPostsState}
            boards={boards}
            customPostFields={customPostFields}
            roadmapPosts={posts}
            hiddenColumnIDs={roadmap?.settings?.hiddenColumnIDs ?? []}
            createRoadmapPost={noFunc}
            deleteRoadmapPost={noFunc}
            error={''}
            groupBy={groupBy?.value}
            loading={false}
            onOpenPost={noFunc}
            roadmap={roadmap}
            selectedPost={undefined}
            onClosePost={noFunc}
            updateServerSettings={noFunc}
          />
        </div>
      </ContentContainer>
    </>
  );
};

export default connect(null, (dispatch: Dispatch) => ({
  reloadRoadmapPosts: (
    queryParams: { [key: string]: unknown },
    supplementalOwners: StrippedUser[]
  ) => {
    return dispatch(reloadRoadmapPosts(queryParams, supplementalOwners));
  },
}))(PublicPrioritizationRoadmap);
